import React from 'react';
import { Box, Heading } from '@wix/design-system';
import { RecommendationLabel } from '@wix/ambassador-premium-domains-v1-recommendation/types';
import { parseDomain } from '@wix/domains-serverless-interface';
import { AiInsightsButton, ComBrRestriction, DomainWithEllipsis, PremiumDomainBadge, PricingSection } from '@wix/domains-search-suite';
import { useIsMobile } from '../../hooks/useIsMobile';
import { HeroInsightBullets } from '../HeroInsightBullets';
import { HeroBusinessEmailExample } from './HeroBusinessEmailExample';
import { TldAlternativesWithPrice } from '../TldAlternativesWithPrice';
import { AvailableNowBadge } from './AvailableNowBadge';
import { Confetti } from './Confetti';
import { hasMatchLabel, MatchLabelBadge } from './MatchLabelBadge';
import { CompactHeroCard } from './CompactHeroCard';
import { HeroCtaButton } from './HeroCtaButton';
import s from './HeroCard.css';
export const HeroCard = _ref => {
  let {
    recommendation,
    onClickGetIt,
    onAiInsightsClick,
    disabled = false,
    dataHook = 'hero-card',
    isResultsPageOptimizations = false,
    Label,
    compact = false,
    badgeLabel,
    ctaPriority = 'primary',
    inlineMobileActions = false,
    businessEmailExample = false,
    isPricingLoading = false,
    PriceNote,
    priceNoteApplies
  } = _ref;
  const isMobile = useIsMobile();
  const {
    domain = '',
    label,
    premium = false,
    price
  } = recommendation;
  const {
    tld
  } = parseDomain(domain);
  const isExactMatch = label === RecommendationLabel.EXACT_MATCH;
  if (compact) {
    return /*#__PURE__*/React.createElement(CompactHeroCard, {
      recommendation: recommendation,
      onClickGetIt: onClickGetIt,
      disabled: disabled,
      dataHook: dataHook,
      isResultsPageOptimizations: isResultsPageOptimizations,
      Label: Label,
      PriceNote: PriceNote,
      badgeLabel: badgeLabel,
      ctaPriority: ctaPriority,
      businessEmailExample: businessEmailExample,
      isPricingLoading: isPricingLoading
    });
  }
  const showMatchBadge = hasMatchLabel(label) && Boolean(label);
  const ctaButton = /*#__PURE__*/React.createElement(HeroCtaButton, {
    size: isMobile ? 'medium' : 'large',
    priority: ctaPriority,
    disabled: disabled,
    onClick: () => onClickGetIt == null ? void 0 : onClickGetIt(recommendation),
    dataHook: `${dataHook}-cta`
  });
  const badgesRow = /*#__PURE__*/React.createElement(Box, {
    direction: "horizontal",
    gap: "SP2",
    verticalAlign: "middle",
    flexWrap: "wrap"
  }, showMatchBadge && label && (Label ? /*#__PURE__*/React.createElement(Label, {
    primary: true,
    label: label,
    domain: domain,
    size: isMobile ? 'tiny' : 'small'
  }) : /*#__PURE__*/React.createElement(MatchLabelBadge, {
    label: label,
    optimized: false
  })), /*#__PURE__*/React.createElement(AvailableNowBadge, null), premium && /*#__PURE__*/React.createElement(PremiumDomainBadge, {
    size: isMobile ? 'tiny' : 'small'
  }));
  const titleHeading = isResultsPageOptimizations ? /*#__PURE__*/React.createElement(Box, {
    direction: "vertical",
    gap: "4px"
  }, /*#__PURE__*/React.createElement("span", {
    className: s.DomainTitleWrapper
  }, /*#__PURE__*/React.createElement(DomainWithEllipsis, {
    maxWidth: isMobile ? '310px' : '440px',
    textComponent: Heading,
    textComponentProps: {
      as: 'h1',
      size: isMobile ? 'large' : 'extraLarge'
    },
    className: s.optimizedHeroTitle,
    dataHook: `${dataHook}-title`
  }, domain), isExactMatch && /*#__PURE__*/React.createElement(Confetti, null)), isExactMatch && /*#__PURE__*/React.createElement(ComBrRestriction, {
    domainName: domain
  })) : /*#__PURE__*/React.createElement(Heading, {
    size: isMobile ? 'large' : 'extraLarge',
    as: "h1",
    dataHook: `${dataHook}-title`
  }, /*#__PURE__*/React.createElement("span", {
    className: s.DomainTitleWrapper
  }, /*#__PURE__*/React.createElement("span", {
    style: {
      fontWeight: isMobile ? 600 : 500
    }
  }, domain), isExactMatch && /*#__PURE__*/React.createElement(Confetti, null)));
  const LeadingBullet = PriceNote && priceNoteApplies != null && priceNoteApplies(price, domain) ? () => /*#__PURE__*/React.createElement(PriceNote, {
    price: price,
    domain: domain,
    bulletAligned: true
  }) : undefined;
  const topSection = /*#__PURE__*/React.createElement(Box, {
    direction: isMobile ? 'vertical' : 'horizontal',
    padding: isMobile ? 'SP6 SP6 16px SP6' : 'SP6',
    align: isMobile ? undefined : 'space-between',
    gap: isMobile ? isResultsPageOptimizations ? '16px' : '10px' : 'SP6'
  }, /*#__PURE__*/React.createElement(Box, {
    direction: "vertical",
    gap: isMobile ? isResultsPageOptimizations ? '16px' : 'SP6' : '10px',
    flex: isMobile ? undefined : 1,
    minWidth: 0
  }, badgesRow, businessEmailExample ? /*#__PURE__*/React.createElement(Box, {
    direction: "vertical",
    gap: "4px"
  }, titleHeading, !isMobile && /*#__PURE__*/React.createElement(HeroBusinessEmailExample, {
    domain: domain
  })) : /*#__PURE__*/React.createElement(React.Fragment, null, titleHeading, isMobile ? PriceNote && /*#__PURE__*/React.createElement(PriceNote, {
    price: price,
    domain: domain
  }) : /*#__PURE__*/React.createElement(Box, {
    paddingTop: LeadingBullet ? 'SP10' : 'SP2'
  }, /*#__PURE__*/React.createElement(HeroInsightBullets, {
    domain: domain,
    LeadingBullet: LeadingBullet
  })))), /*#__PURE__*/React.createElement(Box, {
    direction: "horizontal",
    gap: "SP4",
    verticalAlign: "middle",
    flexShrink: 0,
    width: isMobile ? '100%' : undefined,
    align: isMobile && (isResultsPageOptimizations || inlineMobileActions) ? 'space-between' : undefined,
    flexWrap: isMobile && (isResultsPageOptimizations || inlineMobileActions) ? 'wrap' : undefined,
    alignSelf: isMobile ? undefined : 'flex-end'
  }, /*#__PURE__*/React.createElement(PricingSection, {
    pricing: price,
    tld: tld,
    domainName: domain,
    useMobileLayout: isMobile,
    isResultsPageOptimizations: isResultsPageOptimizations,
    matchStrikethroughSize: isMobile && isResultsPageOptimizations,
    isPricingLoading: isPricingLoading
  }), (!isMobile || isResultsPageOptimizations || inlineMobileActions) && /*#__PURE__*/React.createElement(Box, {
    flexShrink: 0,
    marginLeft: isMobile && (isResultsPageOptimizations || inlineMobileActions) ? 'auto' : undefined
  }, ctaButton)), isMobile && !isResultsPageOptimizations && !inlineMobileActions && /*#__PURE__*/React.createElement(Box, {
    direction: "horizontal",
    align: "space-between",
    verticalAlign: "middle"
  }, onAiInsightsClick ? /*#__PURE__*/React.createElement(AiInsightsButton, {
    onClick: () => onAiInsightsClick(domain)
  }) : /*#__PURE__*/React.createElement(Box, null), ctaButton));
  return /*#__PURE__*/React.createElement(Box, {
    direction: "vertical",
    width: "100%",
    borderRadius: "12px",
    overflow: "hidden",
    className: isResultsPageOptimizations ? s.card : s.cardShadow,
    dataHook: dataHook
  }, topSection, !isExactMatch && /*#__PURE__*/React.createElement(TldAlternativesWithPrice, {
    heroDomain: domain,
    disabled: disabled,
    withDivider: true,
    containerProps: {
      padding: 'SP4 SP6 SP6 SP6',
      className: s.TldAlternatives
    }
  }));
};
//# sourceMappingURL=HeroCard.js.map